import React from 'react';
import styled from 'styled-components';
import { variant } from 'styled-system';
import { number, string, object } from 'prop-types';
import { numberFormat } from '@airasia-common/libraries/i18n/LocaleHelper';
import Row from '../Container/Row';
import Text from './Text';
import { needsSmallVariant } from './utils';

export const Variant = {
  StrikeThrough: 'strikeThrough',
  Price: 'price',
  PaymentItem: 'paymentItem',
  PaymentSubItem: 'paymentSubItem',
  PaymentTotal: 'paymentTotal',
  PaymentTotalSmall: 'paymentTotalSmall',
  ChangeFlightPrice: 'changeFlightPrice',
  bigPoint: 'bigPoint',
  TotalAmount: 'totalAmount',
  PromoDiscount: 'promoDiscount',
  discountItem: 'discountItem',
  MarkerPriceActive: 'activePrice',
  MarkerPriceInactive: 'inactivePrice',
  MarkerPriceSecondaryInactive: 'markerPriceSecondaryInactive',
  NoAvailableHotelInactive: 'noAvailableHotelInactive',
  GuestTotal: 'guestTotal',
  payLaterAmountToPaid: 'payLaterAmountToPaid',
  total: 'total',
  OMS: 'OMS',
  move: 'move',
  rm9Button: 'rm9Button',
  totalSavings: 'totalSavings',
};

const strikeThroughStyled = {
  fontSize: [10, 12],
  lineHeight: '14px',
  fontWeight: 400,
  textDecoration: 'line-through',
  color: 'greyDark',
};

const PriceTextStyle = styled(Text)(
  variant({
    variants: {
      [Variant.StrikeThrough]: strikeThroughStyled,
      [Variant.Price]: {
        fontSize: 24,
        color: 'darkBlack',
      },
      [Variant.PaymentItem]: {
        fontSize: [14, 16],
        color: 'darkBlack',
        lineHeight: '18px',
        fontWeight: 400,
      },
      [Variant.PaymentSubItem]: {
        fontSize: [14, 16],
        color: 'greyDark',
        lineHeight: '18px',
        fontWeight: 400,
      },
      [Variant.PaymentTotal]: {
        fontSize: 20,
        color: 'darkBlack',
        lineHeight: '26px',
      },
      [Variant.PaymentTotalSmall]: {
        fontSize: 16,
        color: 'darkBlack',
        lineHeight: '21px',
      },
      [Variant.ChangeFlightPrice]: {
        fontSize: 20,
        color: 'darkBlack',
        lineHeight: '26px',
        fontWeight: 500,
      },
      [Variant.bigPoint]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        lineHeight: '18px',
        fontWeight: 400,
        marginTop: '8px',
      },
      [Variant.TotalAmount]: {
        fontSize: 14,
        color: 'darkBlack',
        lineHeight: '18px',
        fontWeight: 500,
      },
      [Variant.PromoDiscount]: {
        fontSize: 16,
        color: 'darkBlack',
        lineHeight: '18px',
        fontWeight: 400,
      },
      [Variant.discountItem]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        lineHeight: '18px',
        fontWeight: 400,
        marginTop: '8px',
      },
      [Variant.MarkerPriceActive]: {
        fontSize: 18,
        fontWeight: 500,
        color: 'white',
        lineHeight: '12px',
      },
      [Variant.MarkerPriceSecondaryInactive]: {
        fontSize: 18,
        fontWeight: 400,
        color: 'secondary',
        lineHeight: '12px',
      },
      [Variant.MarkerPriceInactive]: {
        fontSize: 18,
        fontWeight: 500,
        color: 'darkBlack',
        lineHeight: '12px',
      },
      [Variant.GuestTotal]: {
        fontSize: 14,
        fontWeight: 500,
        color: 'greyDarker',
        lineHeight: '12px',
      },
      [Variant.payLaterAmountToPaid]: {
        fontSize: 24,
        color: 'darkBlack',
      },
      [Variant.total]: {
        fontSize: 16,
        color: 'darkBlack',
        fontWeight: '400',
      },
      [Variant.OMS]: {
        fontWeight: 500,
        color: '#4B4FA6',
        fontSize: 12,
      },
      [Variant.NoAvailableHotelInactive]: {
        fontSize: 18,
        fontWeight: 400,
        color: 'greyDarker',
        lineHeight: '12px',
      },
      [Variant.move]: {
        fontWeight: 500,
        color: '#023C40',
        fontSize: 12,
      },
      [Variant.rm9Button]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        lineHeight: '18px',
        fontWeight: 400,
        marginTop: '8px',
      },
      [Variant.totalSavings]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        lineHeight: '18px',
        fontWeight: 400,
        marginTop: '8px',
      },
    },
  })
);

const CurrencyStyle = styled(Text)(
  variant({
    variants: {
      [Variant.StrikeThrough]: strikeThroughStyled,
      [Variant.Price]: {
        fontSize: [12, 14],
        color: 'darkBlack',
        fontWeight: 'normal',
      },
      [Variant.PaymentItem]: {
        fontSize: [10, 14],
        lineHeight: ['13px', '18px'],
        color: 'darkBlack',
        fontWeight: 400,
      },
      [Variant.PaymentSubItem]: {
        fontSize: [10, 14],
        lineHeight: ['13px', '18px'],
        color: 'greyDark',
        fontWeight: 400,
      },
      [Variant.PaymentTotal]: {
        fontSize: 14,
        lineHeight: '18px',
        color: 'darkBlack',
      },
      [Variant.ChangeFlightPrice]: {
        fontSize: 14,
        color: 'darkBlack',
        lineHeight: '18px',
        fontWeight: 500,
      },
      [Variant.bigPoint]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        lineHeight: ['13px', '18px'],
        fontWeight: 400,
        marginTop: '8px',
      },
      [Variant.TotalAmount]: {
        fontSize: 14,
        color: 'darkBlack',
        lineHeight: '18px',
        fontWeight: 500,
      },
      [Variant.PromoDiscount]: {
        fontSize: [10, 14],
        lineHeight: ['13px', '18px'],
        color: 'darkBlack',
        fontWeight: 400,
      },
      [Variant.discountItem]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        lineHeight: ['13px', '18px'],
        fontWeight: 400,
        marginTop: '8px',
      },
      [Variant.MarkerPriceActive]: {
        fontSize: 15,
        color: 'white',
        fontWeight: 400,
        lineHeight: '12px',
      },
      [Variant.MarkerPriceSecondaryInactive]: {
        fontSize: 18,
        fontWeight: 400,
        color: 'secondary',
        lineHeight: '12px',
      },
      [Variant.MarkerPriceInactive]: {
        fontSize: 15,
        color: 'darkBlack',
        fontWeight: 400,
        lineHeight: '12px',
      },
      [Variant.GuestTotal]: {
        fontSize: 12,
        fontWeight: 400,
        color: 'greyDarker',
        lineHeight: '12px',
      },
      [Variant.total]: {
        fontSize: 14,
        fontWeight: 400,
        color: 'darkBlack',
        lineHeight: '18px',
      },
      [Variant.NoAvailableHotelInactive]: {
        fontSize: 18,
        fontWeight: 400,
        color: 'greyDarker',
        lineHeight: '12px',
      },
      [Variant.rm9Button]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        lineHeight: ['13px', '18px'],
        fontWeight: 400,
        marginTop: '8px',
      },
      [Variant.totalSavings]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        lineHeight: ['13px', '18px'],
        fontWeight: 400,
        marginTop: '8px',
      },
    },
  })
);

const TotalStyle = styled(Text)(
  variant({
    variants: {
      [Variant.GuestTotal]: {
        fontSize: 14,
        fontWeight: 500,
        color: 'greyDarker',
        lineHeight: '12px',
      },
    },
  })
);

export const PrefixStyle = styled(Text)(
  variant({
    variants: {
      [Variant.bigPoint]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        paddingRight: '3px',
        marginTop: '8px',
      },
      [Variant.discountItem]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        paddingRight: '3px',
        marginTop: '8px',
      },
      [Variant.PromoDiscount]: {
        fontSize: 14,
        color: 'darkBlack',
        paddingRight: '3px',
      },
      [Variant.rm9Button]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        paddingRight: '3px',
        marginTop: '8px',
      },
      [Variant.totalSavings]: {
        fontSize: 14,
        color: 'tertiaryGreen',
        paddingRight: '3px',
        marginTop: '8px',
      },
    },
  })
);

const Prefix = ({ variant }) => {
  const SpanComponent = PrefixStyle;
  return (
    (variant === Variant.bigPoint ||
      variant === Variant.PromoDiscount ||
      variant === Variant.discountItem ||
      variant === Variant.totalSavings) && (
      <SpanComponent variant={variant}>-</SpanComponent>
    )
  );
};

const PriceText = ({
  currency,
  variant,
  children,
  formatOptions,
  displayTotal,
  ...restProps
}) => {
  const numberFormatted =
    typeof children === 'string'
      ? children
      : numberFormat(children, formatOptions);
  return (
    <Row alignItems="baseline" display="inline-flex" {...restProps}>
      <Prefix variant={variant}></Prefix>
      {currency && variant !== Variant.StrikeThrough && (
        <CurrencyStyle className="currency" variant={variant} marginRight={2}>
          {currency}
        </CurrencyStyle>
      )}
      <PriceTextStyle
        className="price"
        variant={
          needsSmallVariant(variant, numberFormatted)
            ? Variant.PaymentTotalSmall
            : variant
        }
      >
        {variant === Variant.StrikeThrough && `${currency} `}
        {numberFormatted}
      </PriceTextStyle>
      {displayTotal && (
        <TotalStyle className="price" variant={variant} marginLeft={2}>
          {displayTotal}
        </TotalStyle>
      )}
    </Row>
  );
};

PriceText.propTypes = {
  currency: string,
  variant: string,
  children: string | number,
  formatOptions: object,
  displayTotal: string,
};

Prefix.propTypes = {
  variant: string,
};

export default PriceText;
